const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const PrintDriverRefs = require('@/lib/Printing/PrintDriverRefs');
const PrintRouting = require('@/lib/Printing/PrintRouting');
const PrintType = require('@/lib/Printing/PrintType');

class PrintRouter {
    constructor() {
        /**
         * Where documents are sent by default.
         *
         * @type {PrintRouting}
         */
        this.defaultRouting = null;

        /**
         * Where documents of specific types are sent. Each key is the type of document.
         *
         * @see {PrintType}
         *
         * @type {Object<string, PrintRouting>}
         */
        this.typeRoutings = {};

        /**
         * For these print driver refs you can select a printer name to sent it to.
         * Otherwise, we have no control over which printer it goes to from the browser.
         *
         * @private
         * @type {string[]}
         */
        this.canSelectPrinterForDriverRefs = [
            PrintDriverRefs.PRINTER_APP, // Also Agent V3
        ];

        this.log = logFactory('PrintRouter');

        this.reset();
    }

    /**
     * Reset routings to the global default.
     */
    reset() {
        this.defaultRouting = new PrintRouting(PrintDriverRefs.PRINT);
        this.typeRoutings = {};
    }

    /**
     * Create a PrintRouting object from an object of keys/values (probably from a saved config file).
     *
     * @param {PrintRouting|{driverRef: string, printerName: ?string}} routingData
     *
     * @return {PrintRouting}
     */
    createPrintRoutingFromData(routingData) {
        const validDriverRefs = Object.values(PrintDriverRefs);

        // Ensure valid driver ref.
        // Throws an error for invalid refs.
        const driverRef = routingData.driverRef;
        if (driverRef !== null && validDriverRefs.indexOf(driverRef) === -1) {
            throw new Error(`Unknown print driverRef '${driverRef}'`);
        }

        const printerName = this.canSelectPrinterForDriverRef(driverRef) ? routingData.printerName : null;

        return new PrintRouting(driverRef, printerName);
    }

    /**
     * @param {PrintRouting|{driverRef: string, printerName?: ?string}} routingData
     */
    setDefaultRouting(routingData) {
        this.defaultRouting = this.createPrintRoutingFromData(routingData);
    }

    /**
     * Takes an object of routings for print types (loaded from a file), converts them to PrintRouting objects,
     * and stores them as this.routings.
     *
     * @param {Object<{driverRef: string, printerName?: ?string}>} routings
     */
    setTypeRoutings(routings) {
        let newRoutings = {};
        Object.keys(routings).forEach((type) => {
            // Skip unknown print types.
            // This does not throw an error because we may end up with old/new types in a config that we just
            // don't care about anymore/yet.
            if (!PrintType.hasOwnProperty(type)) {
                return;
            }
            let printRouting = this.createPrintRoutingFromData(routings[type]);
            // Skip types that have a null print routing because they should use the default.
            if (printRouting.driverRef) {
                newRoutings[type] = printRouting;
            }
        });
        this.typeRoutings = newRoutings;
    }

    /**
     * @param {string} printType
     */
    clearTypeRouting(printType) {
        if (printType && this.typeRoutings.hasOwnProperty(printType)) {
            delete this.typeRoutings[printType];
        }
    }

    /**
     * @param {string} printType
     *
     * @return {PrintRouting}
     */
    getRoutingForType(printType) {
        if (printType && this.typeRoutings.hasOwnProperty(printType)) {
            return this.typeRoutings[printType];
        }

        return this.defaultRouting;
    }

    /**
     * @param {Device} device
     */
    loadFromDevice(device) {
        this.reset();

        if (!device.printConfig) {
            return;
        }

        let config = JSON.parse(device.printConfig);
        if (!config || !config.hasOwnProperty('routing')) {
            return;
        }

        if (config.routing.hasOwnProperty('default')) {
            this.setDefaultRouting(
                this.createPrintRoutingFromData(config.routing.default)
            );
        }

        if (config.routing.hasOwnProperty('types')) {
            this.setTypeRoutings(config.routing.types);
        }

        this.log.debug('Loaded print config from device.', this);
    }

    /**
     * Returns an object for the config that can be put into a device's "printConfig" field.
     *
     * @return {{default: PrintRouting, types: Object<string, PrintRouting>}}
     */
    exportConfig() {
        return {
            default: this.defaultRouting,
            types: this.typeRoutings,
        };
    }

    /**
     * @param {string} driverRef
     * @return {boolean}
     */
    canSelectPrinterForDriverRef(driverRef) {
        return this.canSelectPrinterForDriverRefs.indexOf(driverRef) !== -1;
    }
}

module.exports = PrintRouter;
